import type { ComponentType } from 'react';
import { toast, type Id, type ToastOptions } from 'react-toastify';
import {
  BiSolidCheckCircle,
  BiSolidError,
  BiSolidInfoCircle,
  BiSolidXCircle,
} from 'react-icons/bi';

/**
 * Small toast helper — docs/design/12-reskin-spec.md §7.2. `AppToastContainer` (mounted once in
 * `AppProviders`) owns placement/RTL/transition; this module owns the per-tone contract (icon,
 * auto-dismiss duration, live-region role) so callers (T95's create/edit/delete success wiring,
 * and any future error-toast caller per §7.3) don't have to repeat it by hand.
 *
 * Usage: `showSuccessToast(t('students.form.createSuccess', { code }))` — always pass an
 * already-translated string, same convention as `Banner`/`StatusChip`. Fall back to
 * `t('toast.genericSuccess')` / `t('toast.genericError')` only when no dedicated per-action
 * string exists yet (§7.4).
 */
export type ToastTone = 'success' | 'info' | 'warning' | 'error';

const TOAST_ICON: Record<ToastTone, ComponentType> = {
  success: BiSolidCheckCircle,
  info: BiSolidInfoCircle,
  warning: BiSolidError,
  error: BiSolidXCircle,
};

/** Auto-dismiss (ms) and live-region role per tone — §7.2 table.
 * `role: 'alert'` implies assertive live-region semantics, `role: 'status'` implies polite —
 * no separate `aria-live` override needed on individual toasts. */
const TOAST_CONFIG: Record<ToastTone, { autoClose: number | false; role: 'status' | 'alert' }> = {
  success: { autoClose: 4000, role: 'status' },
  info: { autoClose: 4000, role: 'status' },
  warning: { autoClose: 6000, role: 'status' },
  error: { autoClose: false, role: 'alert' },
};

function showToast(tone: ToastTone, message: string, options?: ToastOptions): Id {
  const { autoClose, role } = TOAST_CONFIG[tone];
  const Icon = TOAST_ICON[tone];
  return toast(message, {
    type: tone,
    autoClose,
    role,
    icon: <Icon aria-hidden="true" />,
    ...options,
  });
}

export function showSuccessToast(message: string, options?: ToastOptions): Id {
  return showToast('success', message, options);
}

export function showInfoToast(message: string, options?: ToastOptions): Id {
  return showToast('info', message, options);
}

export function showWarningToast(message: string, options?: ToastOptions): Id {
  return showToast('warning', message, options);
}

export function showErrorToast(message: string, options?: ToastOptions): Id {
  return showToast('error', message, options);
}
